import React, { FC, useCallback, useMemo, useState } from 'react';
import useMountEffect from '../../hooks/useMountEffect';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Button, Card, Popconfirm, Skeleton, Table, message } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import moment from 'moment';
import { EmptyState } from '../empty_state/EmptyState';
import { UserName } from '../name_component/user/UserNameComponent';
import { EmailUserName } from '../email_user_name/EmailUserNameComponent';
import { spotPurchasesRestCrudThunksForSupplier } from '../../thunks/spot_purchases_thunks';
import { workOrdersRestCrudThunksForSupplier } from '../../thunks/work_orders_thunks';
import { isSupplierAllowedToAccess, PERMISSION_NAMES } from '../../utils/AuthUtils';
import {
	nullSafeGet,
	nullSafeGetOrElse,
	renderCurrencyByCurrencyId,
} from '../../utils/DataAccessUtils';
import { getFileLinkFromString } from '../../utils/FileAccessUtils';
import { FINISHED_WORK_ORDER_DISPLAY_STATUSES } from '../../constants/WorkOrderDisplayStatuses';
import RecordSpotPurchaseModal from './RecordSpotPurchaseModal';
import { SPOT_PURCHASES_FOR_WO_TC } from '../../constants/SpotPurchaseStatuses';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import SpotPurchaseItemsCell from './SpotPurchaseItemsCell';

interface SpotPurchasesSectionInWorkOrderProps {
	workOrder: any;
	spotPurchases: any;
	currentUser: any;
	companyConfig: any;
	fetchSpotPurchases: (params: any, targetCollection: string) => Promise<any>;
	deleteSpotPurchase: (entity: any) => Promise<any>;
	getWorkOrder: (workOrderId: number | string) => Promise<any>;
}

const SpotPurchasesSectionInWorkOrder: FC<SpotPurchasesSectionInWorkOrderProps> = ({
	workOrder,
	spotPurchases,
	currentUser,
	companyConfig,
	fetchSpotPurchases,
	deleteSpotPurchase,
	getWorkOrder,
}) => {
	const [modalVisible, setModalVisible] = useState(false);
	const [editing, setEditing] = useState<any>(null);
	const [loading, setLoading] = useState(false);

	const workOrderId = nullSafeGet('id', workOrder);
	const supplierCurrencyId = nullSafeGetOrElse('facility.currencyId', currentUser, 'USD');
	const refreshSpotPurchases = useCallback(() => {
		if (!workOrderId) return Promise.resolve();
		setLoading(true);
		return fetchSpotPurchases({ workOrderId }, SPOT_PURCHASES_FOR_WO_TC).finally(() =>
			setLoading(false)
		);
	}, [fetchSpotPurchases, workOrderId]);

	const refreshAfterSpotPurchaseChange = useCallback((): Promise<void> => {
		if (!workOrderId) return Promise.resolve();
		setLoading(true);
		return Promise.all([
			fetchSpotPurchases({ workOrderId }, SPOT_PURCHASES_FOR_WO_TC),
			getWorkOrder(workOrderId),
		])
			.then(() => undefined)
			.finally(() => setLoading(false));
	}, [fetchSpotPurchases, getWorkOrder, workOrderId]);

	useMountEffect(() => {
		refreshSpotPurchases();
	});

	const records = useMemo(
		() => getRecordsForTargetCollection(spotPurchases, SPOT_PURCHASES_FOR_WO_TC) || [],
		[spotPurchases]
	);

	const canViewCost = useMemo(
		() =>
			isSupplierAllowedToAccess(
				PERMISSION_NAMES.CAN_VIEW_PART_EQUIPMENT_COST_IN_WO,
				companyConfig,
				nullSafeGet('roles', currentUser)
			),
		[companyConfig, currentUser]
	);

	const totalCost = useMemo(
		() =>
			records.reduce(
				(acc: number, record: any) => acc + (Number(nullSafeGet('totalCost', record)) || 0),
				0
			),
		[records]
	);

	const openCreate = useCallback(() => {
		setEditing(null);
		setModalVisible(true);
	}, []);

	const openEdit = useCallback((record: any) => {
		setEditing(record);
		setModalVisible(true);
	}, []);

	const closeModal = useCallback(() => setModalVisible(false), []);

	const handleSuccess = useCallback(() => {
		closeModal();
		refreshAfterSpotPurchaseChange().catch((errorMessage) => {
			message.error(errorMessage || 'Failed to refresh spot purchases.');
		});
	}, [closeModal, refreshAfterSpotPurchaseChange]);

	const handleDelete = useCallback(
		(record: any) => () => {
			setLoading(true);
			deleteSpotPurchase(record)
				.then(() => refreshAfterSpotPurchaseChange())
				.then(() => {
					message.success('Spot purchase removed');
				})
				.catch((errorMessage) => {
					message.error(errorMessage || 'Failed to remove spot purchase.');
					setLoading(false);
				});
		},
		[deleteSpotPurchase, refreshAfterSpotPurchaseChange]
	);

	const columns = useMemo(() => {
		const cols: any[] = [
			{
				title: 'Purchased by',
				dataIndex: 'purchasedBy',
				width: 220,
				render: (_: any, record: any) => {
					const purchasedBy =
						nullSafeGet('purchasedByContact', record) || nullSafeGet('purchasedBy', record);
					const email =
						nullSafeGet('purchasedByEmail', record) || nullSafeGet('email', purchasedBy);
					if (purchasedBy) {
						return (
							<UserName
								contact={purchasedBy}
								userType="supplier"
								mode="inline"
								showNameWithAvatar
								underline={false}
							/>
						);
					}
					return email ? <EmailUserName email={email} /> : <span>—</span>;
				},
			},
			{
				title: 'Added On',
				dataIndex: 'purchasedAt',
				width: 140,
				render: (purchasedAt: string, record: any) => {
					const timestamp = purchasedAt || nullSafeGet('createdAt', record);
					return timestamp ? moment(timestamp).format('D MMM, YYYY') : '—';
				},
			},
			{
				title: 'Items',
				dataIndex: 'items',
				render: (items: any[], record: any) => {
					const recordCurrencyId = nullSafeGetOrElse('currencyId', record, supplierCurrencyId);
					const recordTaxAmount = nullSafeGet('taxAmount', record);
					return (
						<SpotPurchaseItemsCell
							items={items || []}
							currencyId={recordCurrencyId}
							taxAmount={recordTaxAmount}
						/>
					);
				},
			},
		];
		if (canViewCost) {
			cols.push({
				title: 'Total',
				dataIndex: 'totalCost',
				width: 160,
				align: 'right' as const,
				render: (cost: number, record: any) => {
					const receiptUrl = nullSafeGet('receiptUrl', record);
					const receiptFileName = nullSafeGet('receiptFileName', record);
					const receiptViewUrl = receiptUrl
						? /^https?:\/\//i.test(receiptUrl)
							? receiptUrl
							: getFileLinkFromString('supplier')(receiptUrl)
						: null;
					return (
						<div className="flex flex-col items-end gap-1">
							<span>{renderCurrencyByCurrencyId(supplierCurrencyId)(cost || 0)}</span>
							{receiptViewUrl ? (
								<a
									className="text-cyan-7 underline hover:text-cyan-7"
									href={receiptViewUrl}
									target="_blank"
									rel="noopener noreferrer"
								>
									view receipt
								</a>
							) : null}
						</div>
					);
				},
			});
		}
		cols.push({
			title: '',
			dataIndex: 'actions',
			width: 100,
			align: 'right' as const,
			render: (_: any, record: any) => (
				<div className="flex items-center justify-end gap-2">
					<Button
						size="small"
						icon={<EditOutlined />}
						onClick={() => openEdit(record)}
						aria-label="Edit spot purchase"
					/>
					<Popconfirm
						title="Remove this spot purchase?"
						okText="Remove"
						cancelText="Cancel"
						onConfirm={handleDelete(record)}
					>
						<Button size="small" icon={<DeleteOutlined />} aria-label="Delete spot purchase" />
					</Popconfirm>
				</div>
			),
		});
		return cols;
	}, [canViewCost, currentUser, openEdit, handleDelete, supplierCurrencyId]);

	if (!workOrderId) {
		return null;
	}

	const hasRecords = records.length > 0;

	return (
		<>
			<Card
				title={`Spot Purchases`}
				extra={
					<div className="flex flex-row items-center">
						{canViewCost && hasRecords ? (
							<div className="mr-2">{`Total Cost: ${renderCurrencyByCurrencyId(supplierCurrencyId)(
								totalCost
							)}`}</div>
						) : null}
						<Button onClick={openCreate}>Add Spot Purchase</Button>
					</div>
				}
			>
				{loading && !hasRecords ? (
					<Table
						columns={columns.map((col) => ({
							...col,
							render: () => <Skeleton paragraph={{ rows: 2 }} title={false} />,
						}))}
						dataSource={[{ id: 1 }, { id: 2 }, { id: 3 }]}
						pagination={false}
						rowKey="id"
					/>
				) : hasRecords ? (
					<Table
						dataSource={records}
						columns={columns}
						pagination={false}
						rowKey="id"
						loading={loading}
					/>
				) : (
					<EmptyState
						graphic={
							<img
								alt="No Spot Purchases"
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/box.svg"
							/>
						}
						headline="No Spot Purchases"
						body={null}
					/>
				)}
			</Card>
			{modalVisible ? (
				<RecordSpotPurchaseModal
					visible={modalVisible}
					workOrder={workOrder}
					spotPurchase={editing}
					onCancel={closeModal}
					onSuccess={handleSuccess}
				/>
			) : null}
		</>
	);
};

const mapStateToProps = (state: any) => ({
	spotPurchases: state.spot_purchases,
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch: any) => ({
	fetchSpotPurchases: (params: any, targetCollection: string) =>
		dispatch(spotPurchasesRestCrudThunksForSupplier.read(params, targetCollection)),
	deleteSpotPurchase: (entity: any) =>
		dispatch(spotPurchasesRestCrudThunksForSupplier.delete(entity)),
	getWorkOrder: (workOrderId: number | string) =>
		dispatch(workOrdersRestCrudThunksForSupplier.readOne(workOrderId)),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(SpotPurchasesSectionInWorkOrder)
);
